import React, { useEffect, useRef, useImperativeHandle, forwardRef, useCallback } from "react";

const FastagMap = forwardRef(({ transactions, selectedTransaction, onTransactionSelect }, ref) => {
  const mapRef           = useRef(null);
  const mapInstanceRef   = useRef(null);
  const olaMapsRef       = useRef(null);
  const markersRef       = useRef([]);
  const mapReadyRef      = useRef(false);   // true once "load" fires
  const pendingDrawRef   = useRef(false);   // draw was requested before map was ready

  /* ── Helpers ── */
  const parseGeocode = (geocode) => {
    if (!geocode) return null;
    const parts = geocode.split(",").map((s) => parseFloat(s.trim()));
    if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
    return [parts[1], parts[0]]; // lat,lng → [lng,lat]
  };

  /* ── Build truck marker DOM element ── */
  const buildMarkerEl = useCallback(({ isFirst, isLast, index, isSelected, txn }) => {
    const bg         = isFirst ? "#059669" : isLast ? "#0369a1" : isSelected ? "#d97706" : "#0f172a";
    const border     = isFirst ? "#059669" : isLast ? "#0369a1" : isSelected ? "#d97706" : "#334155";
    const label      = isFirst ? "S"       : isLast ? "E"       : String(index + 1);
    const labelBg    = isFirst ? "#d1fae5" : isLast ? "#bae6fd" : isSelected ? "#fef3c7" : "#f1f5f9";
    const labelColor = isFirst ? "#065f46" : isLast ? "#075985" : isSelected ? "#92400e" : "#475569";

    const wrapper = document.createElement("div");
    wrapper.style.cssText = "display:flex;flex-direction:column;align-items:center;cursor:pointer;user-select:none;";

    const box = document.createElement("div");
    box.style.cssText = `
      width:36px;height:36px;border-radius:8px;
      background:${bg};border:2px solid ${border};
      display:flex;align-items:center;justify-content:center;
      box-shadow:0 2px 8px rgba(0,0,0,0.22);
      transition:transform 0.15s,box-shadow 0.15s;
      position:relative;
    `;
    box.innerHTML = `
      <svg viewBox="0 0 32 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="width:20px;height:15px;display:block">
        <rect x="1" y="5" width="20" height="14" rx="2" fill="white" opacity="0.95"/>
        <path d="M21 9h5l4 6v5h-9V9z" fill="white" opacity="0.95"/>
        <circle cx="7"  cy="20" r="2.5" fill="${bg}" stroke="white" stroke-width="1.5"/>
        <circle cx="24" cy="20" r="2.5" fill="${bg}" stroke="white" stroke-width="1.5"/>
      </svg>
      <span style="
        position:absolute;top:-7px;right:-7px;
        min-width:16px;height:16px;border-radius:99px;
        background:${labelBg};color:${labelColor};
        font-size:8px;font-weight:700;font-family:sans-serif;
        display:flex;align-items:center;justify-content:center;
        padding:0 3px;border:1.5px solid white;
        box-shadow:0 1px 3px rgba(0,0,0,0.15);
      ">${label}</span>
    `;

    const nameTag = document.createElement("div");
    nameTag.style.cssText = `
      margin-top:3px;padding:1px 5px;border-radius:3px;
      font-size:8px;font-weight:600;font-family:sans-serif;
      background:rgba(15,23,42,0.78);color:#f8fafc;
      white-space:nowrap;max-width:110px;overflow:hidden;text-overflow:ellipsis;
    `;
    nameTag.innerText = txn.tollPlazaName || "";

    wrapper.appendChild(box);
    wrapper.appendChild(nameTag);

    wrapper.onmouseenter = () => { box.style.transform = "scale(1.2)"; box.style.boxShadow = "0 4px 16px rgba(0,0,0,0.3)"; };
    wrapper.onmouseleave = () => { box.style.transform = "scale(1)";   box.style.boxShadow = "0 2px 8px rgba(0,0,0,0.22)"; };

    return wrapper;
  }, []);

  /* ── Core draw function ── */
  const drawMarkersAndRoute = useCallback((txns, selTxn) => {
    const map     = mapInstanceRef.current;
    const olaMaps = olaMapsRef.current;
    if (!map || !olaMaps || !mapReadyRef.current) return;

    // Clear old markers
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    if (!txns?.length) return;

    const sorted = [...txns].sort(
      (a, b) => new Date(a.readerReadTime) - new Date(b.readerReadTime)
    );

    const coords = sorted.map((t) => parseGeocode(t.tollPlazaGeocode)).filter(Boolean);
    if (!coords.length) return;

    // ── Route polyline ──
    const routeGeoJSON = {
      type: "Feature",
      geometry: { type: "LineString", coordinates: coords },
    };

    if (map.getSource("fastag-route")) {
      map.getSource("fastag-route").setData(routeGeoJSON);
    } else {
      map.addSource("fastag-route", { type: "geojson", data: routeGeoJSON });
      map.addLayer({
        id: "fastag-route-shadow", type: "line", source: "fastag-route",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#000", "line-width": 5, "line-opacity": 0.07, "line-translate": [0, 2] },
      });
      map.addLayer({
        id: "fastag-route-line", type: "line", source: "fastag-route",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: {
          "line-color": "#d97706", "line-width": 3,
          "line-opacity": 0.85, "line-dasharray": [2, 1.5],
        },
      });
    }

    // ── Truck markers ──
    sorted.forEach((txn, index) => {
      const lngLat = parseGeocode(txn.tollPlazaGeocode);
      if (!lngLat) return;

      const el = buildMarkerEl({
        isFirst:    index === 0,
        isLast:     index === sorted.length - 1,
        index,
        isSelected: selTxn?.seqNo === txn.seqNo,
        txn,
      });

      el.onclick = (e) => {
        e.stopPropagation();
        onTransactionSelect?.(txn);
      };

      const marker = olaMaps
        .addMarker({ element: el, anchor: "bottom" })
        .setLngLat(lngLat)
        .addTo(map);

      markersRef.current.push(marker);
    });
  }, [buildMarkerEl, onTransactionSelect]);

  /* ── Imperative API ── */
  useImperativeHandle(ref, () => ({
    focusOnRoute: (txns) => {
      const map = mapInstanceRef.current;
      if (!map || !txns?.length) return;
      const coords = txns.map((t) => parseGeocode(t.tollPlazaGeocode)).filter(Boolean);
      if (!coords.length) return;
      if (coords.length === 1) { map.flyTo({ center: coords[0], zoom: 12, duration: 1000 }); return; }
      let minLng = 180, maxLng = -180, minLat = 90, maxLat = -90;
      coords.forEach(([lng, lat]) => {
        minLng = Math.min(minLng, lng); maxLng = Math.max(maxLng, lng);
        minLat = Math.min(minLat, lat); maxLat = Math.max(maxLat, lat);
      });
      map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 80, duration: 1000 });
    },
    focusOnTransaction: (txn) => {
      const map = mapInstanceRef.current;
      if (!map || !txn) return;
      const coords = parseGeocode(txn.tollPlazaGeocode);
      if (!coords) return;
      map.flyTo({ center: coords, zoom: 14, duration: 1000 });
    },
  }));

  /* ── Init map — store latest transactions ref so the load callback can use them ── */
  const transactionsRef      = useRef(transactions);
  const selectedTxnRef       = useRef(selectedTransaction);
  transactionsRef.current    = transactions;
  selectedTxnRef.current     = selectedTransaction;

  useEffect(() => {
    let mounted = true;
    const loadMap = async () => {
      const { OlaMaps } = await import("olamaps-web-sdk");
      if (!mounted) return;

      const olaMaps = new OlaMaps({ apiKey: process.env.REACT_APP_OLA_API_KEY });
      const map = olaMaps.init({
        container: mapRef.current,
        style: "https://api.olamaps.io/tiles/vector/v1/styles/default-light-standard/style.json",
        center: [77.23, 28.61],
        zoom: 5,
      });

      mapInstanceRef.current = map;
      olaMapsRef.current     = olaMaps;

      map.on("load", () => {
        if (!mounted) return;
        mapReadyRef.current = true;
        // If transactions arrived before the map was ready, draw them now
        if (pendingDrawRef.current) {
          pendingDrawRef.current = false;
          drawMarkersAndRoute(transactionsRef.current, selectedTxnRef.current);
        }
      });
    };
    loadMap();
    return () => { mounted = false; };
  }, []); // runs once

  /* ── Redraw whenever transactions or selection changes ── */
  useEffect(() => {
    if (!mapReadyRef.current) {
      // Map not ready yet — flag so the load handler draws when ready
      if (transactions?.length) pendingDrawRef.current = true;
      return;
    }
    drawMarkersAndRoute(transactions, selectedTransaction);
  }, [transactions, selectedTransaction, drawMarkersAndRoute]);

  return <div ref={mapRef} style={{ width: "100%", height: "100%", flex: 1 }} />;
});

FastagMap.displayName = "FastagMap";
export default FastagMap;